import { MyAreaBookingPaymentProps, PaymentInfoSelected } from '@msc-b2c/interfaces/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  breakpoints,
  closeLoader,
  errorModalOpen,
  formatPrice,
  getActiveSession,
  getCreditCards,
  getCurrencyCode,
  getCurrencySymbol,
  getOtherPaymentMethods,
  getPaymentButtons,
  getPriceCurrencyOrder,
  getQueryParam,
  handleKeyDown,
  managePlurals,
  mscGet,
  myAreaFinalizeBookingPayment,
  openLoader,
  replacePlaceholders,
  RootState,
  selectedPaymentInfo,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  FinalizePaymentMyMscResponse,
  PriceToBookCruiseMyMscPaymentOptionsI,
} from '@msc-b2c/interfaces/interfaces';
import { BookingStatusEnum, PaymentTypeEnum } from '@msc-b2c/interfaces/enums';
import dayjs from 'dayjs';
import { Icon, PaymentSection, SkeletonLoader, Text } from '@msc-b2c/shared-components';
import { PaymentConfirmation } from './paymentconfirmation/PaymentConfirmation';
import styles from './payment.module.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorModal } from '../errormodal/ErrorModal';
import {
  getPaymentTypeFromSession,
  triggerBookingAddPaymentInfoEvent,
  triggerBookingPaymentFailureEvent,
} from '../utils/myAreaAnalytics';

export function Payment({
  paymentMainTitle,
  paymentMethodsTitle,
  paymentMethodsDescription,
  depositSectionTitle,
  depositSectionDescription,
  fullAmountSectionTitle,
  fullAmountDescription,
  dateFormat,
  cardPaymentDetailsLabel,
  cardNumberPlaceholder,
  cardExpirationDatePlaceholder,
  cardCvvPlaceholder,
  cardCvvDisclaimer,
  termsAndConditionSection,
  paymentButtonCTA,
  refundableDepositBanner,
  summaryTitle,
  fullAmountSummaryText,
  depositSummaryText,
  depositBookingText,
  amountPaidLabel,
  datatrans,
  numberOfItem,
  paymentSummaryClosedAccordionIcon,
  paymentSummaryOpenAccordionIcon,
  confirmation,
  firstPaymentType,
  defaultPaymentType,
  errorModalTitle,
  errorModalDescription,
  errorModalCTALabel,
  mandatoryLabel,
}: Readonly<MyAreaBookingPaymentProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const currency = getCurrencySymbol();
  const { type, price } = useSelector((state: RootState) => state.payment.selectedPayment) || {};
  const isErrorModalOpen = useSelector((state: RootState) => state.payment.isErrorModalOpen);
  const priceToBookResponse: PriceToBookCruiseMyMscPaymentOptionsI[] | null = useSelector(
    (state: RootState) => state.myCruise.bookingPayment
  );

  const finalizeBookingPaymentResponse: FinalizePaymentMyMscResponse | null = useSelector(
    (state: RootState) => state.myCruise.finalizeBookingPayment
  );
  const { membershipData, cabinDetails } = useSelector((state: RootState) => state.myCruise);
  const voyagersClub = membershipData?.memberships;

  const ITEMS_FROM_CART_SELECTOR = 1;
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoints.lg : false
  );
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const toggle = () => {
    setIsAccordionOpen((prev) => !prev);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoints.lg);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(myAreaFinalizeBookingPayment(null));
    };
  }, []);

  const { bookingNumber = '', passengerId = '', email } = getActiveSession();
  // GET BOOKING API CALL
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoints.lg);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // calculate the percentage of the deposit based on the total price
  const fullPrice =
    priceToBookResponse?.find((priceObject) => priceObject.type === PaymentTypeEnum.FULL)?.due
      .gross ?? '';
  const depositPrice =
    priceToBookResponse?.find((priceObject) => priceObject.type === PaymentTypeEnum.DEPOSIT)?.due
      .gross ?? '';

  // check if some previous payment was made in the booking
  const alreadyPaid = booking?.total?.received && Number(booking?.total?.received) > 0;

  // if in the response there is an object with type = "DEPOSIT" then enable deposit and the amount is different from the full amount
  const isDepositEnabled = !!(depositPrice && depositPrice !== fullPrice) && !alreadyPaid;
  const isFullPaymentEnabled = !!fullPrice;

  // Auto select only when a single payment option is available
  const hasOnlyOnePaymentOption =
    (isFullPaymentEnabled && !isDepositEnabled) || (!isFullPaymentEnabled && isDepositEnabled);
  const paymentTypeToSelect = hasOnlyOnePaymentOption
    ? isDepositEnabled
      ? PaymentTypeEnum.DEPOSIT
      : PaymentTypeEnum.FULL
    : '';
  const defaultPaymentMethod = getCreditCards() ? 'card' : getPaymentButtons() ? 'button' : 'other';
  const defaultPriceInSummary =
    paymentTypeToSelect === PaymentTypeEnum.DEPOSIT
      ? depositPrice
      : paymentTypeToSelect === PaymentTypeEnum.FULL
      ? fullPrice
      : defaultPaymentType === PaymentTypeEnum.DEPOSIT && isDepositEnabled
      ? depositPrice
      : fullPrice;

  const mopAvailable = useMemo(() => {
    const creditCardCodes = getCreditCards().map((paymentInfo) => paymentInfo.type);
    const paymentButtonCodes = getPaymentButtons().map((paymentInfo) => paymentInfo.type);
    const otherPaymentCodes = getOtherPaymentMethods().map((paymentInfo) => paymentInfo.type);
    return [...creditCardCodes, ...paymentButtonCodes, ...otherPaymentCodes]
      .filter((code) => !!code)
      .map((code) => code.toLowerCase())
      .join('|');
  }, []);

  const hasTriggeredAddPaymentInfoRef = useRef(false);
  const trackedPaymentFailureTransactionRef = useRef<string | null>(null);
  const [result3dsPayment, setResult3dsPayment] = useState<{
    status: string | null;
    transactionId: string | null;
    amount: string | null;
    paymentType: string | null;
  } | null>(() => {
    const status = getQueryParam('status_3d');
    const transactionId = getQueryParam('uppTransactionId');
    const amount = status ? mscGet<string>('session', 'paymentAmount') : null;
    const paymentType = status ? mscGet<string>('session', 'paymentType') : null;

    return {
      status,
      transactionId,
      amount,
      paymentType,
    };
  });

  useEffect(() => {
    if (
      !booking ||
      !priceToBookResponse ||
      hasTriggeredAddPaymentInfoRef.current ||
      !cabinDetails ||
      (finalizeBookingPaymentResponse?.transactionId && !finalizeBookingPaymentResponse?._warnings)
    ) {
      return;
    }

    triggerBookingAddPaymentInfoEvent({
      booking,
      loggedUserId: passengerId,
      cabinDetails,
      currency: getCurrencyCode(),
      value: defaultPriceInSummary || '0',
      mopAvailable,
      appliedECoupon: '',
      voyagersClub,
    });
    hasTriggeredAddPaymentInfoRef.current = true;
  }, [
    booking,
    priceToBookResponse,
    finalizeBookingPaymentResponse,
    cabinDetails,
    defaultPriceInSummary,
    mopAvailable,
    passengerId,
  ]);

  useEffect(() => {
    const isFailed3dsPaymentReturn = result3dsPayment?.status === 'N';
    const failed3dsPaymentAmount = result3dsPayment?.amount;
    const failed3dsPaymentType = result3dsPayment?.paymentType;

    const hasPaymentFailureWarning = !!finalizeBookingPaymentResponse?._warnings?.length;
    const transactionId =
      result3dsPayment?.transactionId || finalizeBookingPaymentResponse?.transactionId || '';

    if (
      !booking ||
      (!price && !failed3dsPaymentAmount) ||
      (!hasPaymentFailureWarning && !isFailed3dsPaymentReturn) ||
      trackedPaymentFailureTransactionRef.current === transactionId ||
      !cabinDetails
    ) {
      return;
    }

    triggerBookingPaymentFailureEvent({
      booking,
      loggedUserId: passengerId,
      transactionId,
      transactionType:
        booking?.status.code === BookingStatusEnum.OPTIONAL
          ? failed3dsPaymentType || type
          : 'balance',
      paymentType: getPaymentTypeFromSession(),
      paymentAmount: failed3dsPaymentAmount || price || '',
      membershipData,
      cabinDetails,
      totalDiscount: 0,
      checkoutPaymentEmail: email,
    });
    trackedPaymentFailureTransactionRef.current = transactionId;
    setResult3dsPayment(null);
  }, [
    booking,
    cabinDetails,
    email,
    result3dsPayment,
    finalizeBookingPaymentResponse,
    membershipData,
    passengerId,
    price,
  ]);

  const summaryTotalPrice =
    alreadyPaid && price ? formatPrice(booking?.total?.gross.replace(',', '.')) : price;

  const handleModalClose = () => {
    dispatch(errorModalOpen(false));
  };

  useEffect(() => {
    if (!mscGet<string>('session', 'paymentAmount') && !price) {
      const shouldAutoSelect = !!paymentTypeToSelect;
      const firstLoadPaymentInfo: PaymentInfoSelected = {
        type: paymentTypeToSelect,
        price: shouldAutoSelect ? defaultPriceInSummary : '',
        paymentMethod: shouldAutoSelect ? defaultPaymentMethod : '',
      };
      dispatch(selectedPaymentInfo(firstLoadPaymentInfo));
      closeLoader('.loader');
    } else if (
      mscGet<string>('session', 'paymentAmount') &&
      mscGet<string>('session', 'paymentType')
    ) {
      if (!finalizeBookingPaymentResponse?._warnings && finalizeBookingPaymentResponse)
        openLoader('.loader');
      const paymentInfoInSession: PaymentInfoSelected = {
        type: mscGet<string>('session', 'paymentType'),
        price: mscGet<string>('session', 'paymentAmount'),
        paymentMethod: defaultPaymentType ? defaultPaymentMethod : '',
      };
      dispatch(selectedPaymentInfo(paymentInfoInSession));
    }
  }, [priceToBookResponse, finalizeBookingPaymentResponse]);

  // get the dueDate field value from the object with type = "DEPOSIT" in priceToBookResponse
  const dueDateRefundableDeposit = isDepositEnabled
    ? priceToBookResponse?.find((priceObject) => priceObject.type === PaymentTypeEnum.DEPOSIT)
        ?.dueDate
    : '';
  const dueDateFullPayment = isFullPaymentEnabled
    ? priceToBookResponse?.find((priceObject) => priceObject.type === PaymentTypeEnum.FULL)?.dueDate
    : '';
  let depositDescription = depositSectionDescription;
  let depositRefundableBannerEnabled = false;
  let refundableDepositBannerDescription = refundableDepositBanner?.description;
  let depositTransactionId: string | undefined = '';
  const fullTransactionId = priceToBookResponse?.find(
    (priceObject) => priceObject.type === PaymentTypeEnum.FULL
  )?.transactionId;
  if (isDepositEnabled) {
    const depositDueDateFormatted = dayjs(dueDateRefundableDeposit, 'YYYY-MM-DD').format(
      dateFormat
    );
    const remainingBalanceDueDateFormatted = dayjs(dueDateFullPayment, 'YYYY-MM-DD').format(
      dateFormat
    );
    const depositPercentage =
      fullPrice && depositPrice ? (Number(depositPrice) / Number(fullPrice)) * 100 : 0;
    depositDescription = replacePlaceholders(depositSectionDescription, {
      depositAmount: depositPercentage.toFixed(2),
      remainingBalanceAfterDeposit: fullPrice
        ? (Number(fullPrice) - Number(depositPrice)).toFixed(2)
        : '0.00',
      remainingBalanceDueDate: remainingBalanceDueDateFormatted,
    });
    // if date of deposit is in the past, set the depositRefundableBannerEnabled to false
    depositRefundableBannerEnabled =
      dayjs(dueDateRefundableDeposit, 'YYYY-MM-DD').isAfter(dayjs()) && depositPercentage > 0;
    refundableDepositBannerDescription = replacePlaceholders(refundableDepositBanner.description, {
      refundableDepositDueDate: depositDueDateFormatted,
    });

    depositTransactionId = priceToBookResponse?.find(
      (priceObject) => priceObject.type === PaymentTypeEnum.DEPOSIT
    )?.transactionId;
  }

  const allPaymentSections = [
    {
      type: PaymentTypeEnum.DEPOSIT,
      enabled: isDepositEnabled,
      props: {
        type: PaymentTypeEnum.DEPOSIT,
        sectionTitle: depositSectionTitle,
        sectionDescription: depositDescription,
        dateFormat,
        cardPaymentDetailsLabel,
        cardNumberPlaceholder,
        cardExpirationDatePlaceholder,
        cardCvvPlaceholder,
        cardCvvDisclaimer,
        termsAndConditionSection,
        paymentButtonCTA,
        refundableDepositBannerDescription: depositRefundableBannerEnabled
          ? refundableDepositBannerDescription
          : undefined,
        refundableDepositBannerBackgroundColor: depositRefundableBannerEnabled
          ? refundableDepositBanner?.backgroundColor
          : undefined,
        refundableDepositBannerTextColor: depositRefundableBannerEnabled
          ? refundableDepositBanner?.textColor
          : undefined,
        transactionId: depositTransactionId ?? '',
        totalAmount: depositPrice,
        datatrans,
        mandatoryLabel,
      },
    },
    {
      type: PaymentTypeEnum.FULL,
      enabled: isFullPaymentEnabled,
      props: {
        type: PaymentTypeEnum.FULL,
        sectionTitle: fullAmountSectionTitle,
        sectionDescription: fullAmountDescription,
        dateFormat,
        cardPaymentDetailsLabel,
        cardNumberPlaceholder,
        cardExpirationDatePlaceholder,
        cardCvvPlaceholder,
        cardCvvDisclaimer,
        termsAndConditionSection,
        paymentButtonCTA,
        transactionId: fullTransactionId ?? '',
        totalAmount: fullPrice,
        datatrans,
        mandatoryLabel,
      },
    },
  ];

  // Sort sections based on the firstPaymentType
  const sortedSections = allPaymentSections
    .filter((section) => section.enabled)
    .sort((a, b) => (a.type === firstPaymentType ? -1 : 1));

  return finalizeBookingPaymentResponse?.transactionId &&
    !finalizeBookingPaymentResponse?._warnings ? (
    <PaymentConfirmation {...confirmation} />
  ) : (
    <div className={styles['payment_component']}>
      {isErrorModalOpen && (
        <ErrorModal
          title={errorModalTitle}
          message={errorModalDescription}
          ctaLabel={errorModalCTALabel}
          iconType=""
          handleClick={() => handleModalClose()}
          closingIcon=""
        />
      )}

      <Text
        text={paymentMainTitle}
        styleOption={'header-m'}
        tagHtml={'h1'}
      />
      <div className={styles['payment__container']}>
        <div className={styles['payment__options']}>
          {((isMobile && isDepositEnabled && isFullPaymentEnabled) || !isMobile) && (
            <div className={styles['payment__options-title']}>
              <Text
                text={paymentMethodsTitle}
                styleOption={'header-xs'}
              />
              {isDepositEnabled && isFullPaymentEnabled && (
                <Text
                  text={paymentMethodsDescription}
                  styleOption={styles['payment__options-description']}
                />
              )}
            </div>
          )}

          {/* <!-- Payment Section - Deposit or Total ordered --> */}
          {priceToBookResponse ? (
            sortedSections.map((section) => (
              <PaymentSection
                key={section.type}
                {...section.props}
              />
            ))
          ) : (
            <SkeletonLoader
              height={'300px'}
              styleOption="cmp-skeleton__loader"
            />
          )}
        </div>

        {/* <!-- Payment - Summary --> */}
        <div className={styles['payment__summary-container']}>
          {/* <!-- Summary - price details section --> */}
          <div
            className={styles['payment__summary-label-container']}
            onClick={toggle}
            onKeyDown={(event) => handleKeyDown(event, toggle)}
          >
            <Text
              styleOption={'header-xs'}
              text={summaryTitle}
            />
            {price && isMobile && (
              <div className={styles['payment__summary-label-container-price']}>
                <Text
                  styleOption={styles['payment__summary-label-container-price-item']}
                  tagHtml={'span'}
                  text={replacePlaceholders(managePlurals(ITEMS_FROM_CART_SELECTOR, numberOfItem), {
                    numberOfItem: ITEMS_FROM_CART_SELECTOR.toString(),
                  })}
                />
                <Text
                  styleOption={`body-default ${styles['payment__summary-label-container-price-value']}`}
                  text={replacePlaceholders(getPriceCurrencyOrder(), {
                    currency: currency,
                    price: formatPrice(price),
                  })}
                />
                <Icon
                  styleOption={styles['payment__summary-label-container-icon']}
                  icon={
                    isAccordionOpen
                      ? paymentSummaryOpenAccordionIcon ?? 'up'
                      : paymentSummaryClosedAccordionIcon ?? 'down'
                  }
                />
              </div>
            )}
          </div>
          {price && (!isMobile || isAccordionOpen) && (
            <div className={styles['payment__summary-content']}>
              <div className={styles['payment__summary']}>
                {isMobile && (
                  <Text
                    styleOption={'body-default bold'}
                    text={summaryTitle}
                  />
                )}
                <div className={styles['payment__summary-priceContainer']}>
                  <Text
                    styleOption={'body-default'}
                    text={
                      type === PaymentTypeEnum.DEPOSIT ? depositSummaryText : fullAmountSummaryText
                    }
                  />
                  <span className={styles.price}>
                    {replacePlaceholders(getPriceCurrencyOrder(), {
                      currency: currency,
                      price: formatPrice(summaryTotalPrice ?? ''),
                    })}
                  </span>
                </div>
                {alreadyPaid && (
                  <div className={styles['payment__summary-priceContainer']}>
                    <Text
                      styleOption={'body-default'}
                      text={amountPaidLabel}
                    />
                    <span className={styles.price}>
                      {replacePlaceholders(getPriceCurrencyOrder(), {
                        currency: currency,
                        price: formatPrice(booking?.total?.received ?? ''),
                      })}
                    </span>
                  </div>
                )}
              </div>
              <hr className="cmp-separator__horizontal-rule" />
              <div className={styles['payment__summary_total-container']}>
                <Text
                  text={depositBookingText}
                  styleOption={'body-default bold'}
                />
                <Text
                  styleOption={`body-big bold`}
                  text={replacePlaceholders(getPriceCurrencyOrder(), {
                    currency: currency,
                    price: formatPrice(price),
                  })}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
